package com.appsyde.shared.modules.ticket.data.source

import com.appsyde.shared.modules.ticket.domain.model.InboxStats
import com.appsyde.shared.network.exception.InsufficientScopesException
import com.appsyde.shared.network.exception.NotAuthenticatedException
import com.appsyde.shared.network.requireSuccessOrBody
import io.ktor.client.HttpClient
import io.ktor.client.request.accept
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

interface EmailRemoteDataSource {
    suspend fun getUnreadEmailsCount(): Int
    suspend fun getInboxStats(): InboxStats
    suspend fun markAsRead(messageId: String): Boolean
}

class EmailRemoteDataSourceImpl(
    private val client: HttpClient,
    private val serverBaseUrl: String,
) : EmailRemoteDataSource {

    private val json = Json { ignoreUnknownKeys = true }

    private fun log(message: String) {
        println("[EmailRemoteDataSource] $message")
    }

    private fun buildUrl(path: String): String {
        val base = serverBaseUrl.removeSuffix("/")
        return "$base/$path"
    }

    private fun attachAuth(builder: io.ktor.client.request.HttpRequestBuilder) {
        // no-op for cookie-based auth
    }

    override suspend fun getUnreadEmailsCount(): Int {
        val url = buildUrl("inbox/unreadCount")
        log("Fetching UNREAD count GET $url")

        return try {
            val response = client.get(url) {
                attachAuth(this)
                accept(ContentType.Application.Json)
            }

            val raw = response.requireSuccessOrBody(fallbackCode = "unread_count_failed")
            val payload = json.decodeFromString<Map<String, Int>>(raw)
            val count = payload["unread"] ?: 0
            log("Unread count = $count")
            count
        } catch (e: NotAuthenticatedException) {
            throw e
        } catch (e: InsufficientScopesException) {
            throw e
        } catch (e: Exception) {
            log("Error fetching unread count (soft): ${e.message}")
            0
        }
    }

    override suspend fun getInboxStats(): InboxStats {
        val url = buildUrl("inbox/stats")
        log("Fetching inbox stats GET $url")

        return try {
            val response = client.get(url) {
                attachAuth(this)
                accept(ContentType.Application.Json)
            }
            val raw = response.requireSuccessOrBody(fallbackCode = "inbox_stats_failed")
            val obj = json.parseToJsonElement(raw).jsonObject
            fun int(key: String): Int = obj[key]?.jsonPrimitive?.content?.toIntOrNull() ?: 0
            fun intOrNull(key: String): Int? = obj[key]?.jsonPrimitive?.content?.toIntOrNull()
            fun str(key: String, default: String): String = obj[key]?.jsonPrimitive?.content?.trim('"') ?: default
            InboxStats(
                unread = int("unread"),
                processedToday = int("processedToday"),
                openCount = int("openCount"),
                waitingCount = int("waitingCount"),
                clarificationsSuggestedCount = int("clarificationsSuggestedCount"),
                weather = str("weather", "normal"),
                weatherPercentDelta = intOrNull("weatherPercentDelta")
            )
        } catch (e: NotAuthenticatedException) {
            throw e
        } catch (e: InsufficientScopesException) {
            throw e
        } catch (e: Exception) {
            log("Error fetching inbox stats: ${e.message}")
            throw e
        }
    }

    override suspend fun markAsRead(messageId: String): Boolean {
        val url = buildUrl("inbox/markAsRead")
        val mid = messageId.trim()
        log("Sending POST $url (markAsRead) messageId=$mid")

        if (mid.isBlank()) return false

        return try {
            val response = client.post(url) {
                attachAuth(this)
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                setBody(mapOf("messageId" to mid))
            }

            runCatching { response.requireSuccessOrBody("mark_as_read_failed") }.getOrNull() != null
        } catch (e: Exception) {
            log("MarkAsRead call failed: ${e.message}")
            false
        }
    }
}
